import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import { billing_account_id, datetime, int, simpleSuccessResponse, user_id, uuid, vm_uuid } from "./common.ts";
import { address_path, IpAddress, IpAddressAssignBody, IpListResponse, VpcListResponse } from "./network.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

const IpPoolResponse = z
    .object({
        used_ips: int.describe("Floating IP addresses currently allocated from the pools"),
        total_ips: int.describe("Total floating IP capacity across the pools"),
        percent_used: z.number().describe("Percentage of pool capacity currently allocated"),
    })
    .openapi({ ref: "IpPoolResponse" });

const ip_type_query = z
    .enum(["private", "public"])
    .optional()
    .describe("IP type filter. When omitted, only `public` IPs are returned.")
    .openapi({ param: { name: "type", in: "query" } });

const vm_uuid_query = vm_uuid
    .optional()
    .describe("Return only the floating IP currently assigned to this virtual machine")
    .openapi({ param: { name: "vm_uuid", in: "query" } });

const billing_account_id_query = unref(billing_account_id)
    .optional()
    .describe("Return only IPs billed to this billing account")
    .openapi({ param: { name: "billing_account_id", in: "query" } });

const include_deleted_query = z
    .boolean()
    .optional()
    .describe("Include deleted IPs. By default, only non-deleted IPs are returned.")
    .openapi({ param: { name: "include_deleted", in: "query" } });

const deleted_cutoff_date_query = datetime
    .optional()
    .describe("When `include_deleted=true`, return only IPs deleted after this UTC timestamp")
    .openapi({ param: { name: "deleted_cutoff_date", in: "query" } });

const cache_invalidate_query = z
    .boolean()
    .optional()
    .describe("Invalidate cached pool statistics before returning the current status")
    .openapi({ param: { name: "cache_invalidate", in: "query" } });

const action_log_address_param = z
    .string()
    .describe("Floating IP UUID, literal IP address, or `query_by_assigned_to` to switch the log query mode")
    .openapi({ param: { name: "address", in: "path" } });

const assigned_to_uuid_query = uuid
    .optional()
    .describe(
        "Required when `{address}` is `query_by_assigned_to`; returns assign/unassign events for the given VM or load balancer",
    )
    .openapi({ param: { name: "assigned_to_uuid", in: "query" } });

const AdminIpAddressCreateBody = z
    .object({
        user_id: unref(user_id).describe("User that will own the floating IP"),
        billing_account_id,
        address: z
            .string()
            .ip()
            .optional()
            .describe("Specific floating IP to allocate. Omit to auto-allocate the next available address"),
    })
    .openapi({ ref: "AdminIpAddressCreateBody" });

const AdminIpAddressAssignBody = z
    .object({
        ...IpAddressAssignBody.shape,
        vm_uuid: vm_uuid.optional().describe("Shortcut for assigning the floating IP to a VM by UUID"),
    })
    .openapi({
        ref: "AdminIpAddressAssignBody",
    });

const IpAddressLogAction = z
    .enum(["create", "assign", "unassign", "delete"])
    .describe("Action performed on the floating IP during this lifecycle event")
    .openapi({ ref: "IpAddressLogAction" });

const IpAddressActionLogItem = z
    .object({
        uuid: uuid.describe("Floating IP UUID whose lifecycle this log entry belongs to"),
        action: IpAddressLogAction,
        address: z.string().ip(),
        user_id: unref(user_id).describe("Owner of the floating IP when this event was recorded"),
        acting_user_id: unref(user_id).optional().describe("User who triggered the action when it was recorded"),
        vm_uuid: vm_uuid.optional().describe("Virtual machine involved in assign or unassign actions"),
        lb_instance_uuid: uuid.optional().describe("Load balancer involved in assign or unassign actions"),
        created_at: datetime,
    })
    .openapi({ ref: "IpAddressActionLogItem" });

const IpAddressActionLogResponse = z.array(IpAddressActionLogItem).openapi({ ref: "IpAddressActionLogResponse" });

export const networkAdminPaths = tagPaths("admin_network")({
    "/{location}/network/admin/ip_addresses": {
        get: {
            summary: "List all floating IPs",
            parameters: [
                params.location,
                ip_type_query,
                vm_uuid_query,
                billing_account_id_query,
                include_deleted_query,
                deleted_cutoff_date_query,
            ],
            responses: {
                ...successResponse(IpListResponse),
            },
        },
        post: {
            summary: "Create floating IP for a user",
            description: ind`
                Creates a floating IP for the specified user and billing account.

                When \`address\` is omitted, the next available address is allocated automatically.
            `,
            parameters: [params.location],
            requestBody: jsonBody(AdminIpAddressCreateBody),
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },

    "/{location}/network/admin/ip_addresses/{address}/log": {
        get: {
            summary: "Get floating IP action log",
            description: ind`
                Returns action log entries for one or many floating IP lifecycles, depending on \`{address}\`.

                Use one of these lookup modes:
                1. Set \`{address}\` to a floating IP UUID to return one lifecycle, starting from create and possibly ending in delete.
                2. Set \`{address}\` to a literal IP address to return all lifecycles for that address across different users.
                3. Set \`{address}\` to \`query_by_assigned_to\` and provide \`assigned_to_uuid\` to return assign and unassign events for one VM or load balancer.
            `,
            parameters: [params.location, action_log_address_param, assigned_to_uuid_query],
            responses: {
                ...successResponse(IpAddressActionLogResponse),
            },
        },
    },

    "/{location}/network/admin/ip_pool_status": {
        get: {
            summary: "IP pool status",
            parameters: [params.location, cache_invalidate_query],
            responses: {
                ...successResponse(IpPoolResponse),
            },
        },
    },

    "/{location}/network/admin/networks": {
        get: {
            summary: "List all networks",
            parameters: [params.location],
            responses: {
                ...successResponse(VpcListResponse),
            },
        },
    },

    "/{location}/network/admin/ip_addresses/{address}/assign": {
        post: {
            summary: "Assign floating IP",
            description:
                "Assign by resource target using `assigned_to_resource_type` and `assigned_to`. " +
                "Also, a single `vm_uuid` parameter can be used to assign to VM.",
            parameters: [params.location, address_path],
            requestBody: jsonBody(AdminIpAddressAssignBody),
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },
    "/{location}/network/admin/ip_addresses/{address}/unassign": {
        post: {
            summary: "Unassign floating IP",
            parameters: [params.location, address_path],
            responses: {
                ...successResponse(IpAddress),
            },
        },
    },
    "/{location}/network/admin/ip_addresses/{address}": {
        delete: {
            summary: "Delete floating IP",
            parameters: [params.location, address_path],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
});
